/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import mapboxgl, { type Map } from 'mapbox-gl';

import { useBreakpoint } from 'sarsaparilla';

import { useMap } from './MapContext';

import type { MapMarkerItem } from './map.types';

const DEFAULT_MAX_WIDTH = '280px';

// Offset (in pixels) from the marker's anchor point to the popup tip. The
// consumer's CSS may add further padding on top of this.
const POPUP_OFFSET = 16;

// Below this zoom the marker renders as a circle; at or above it renders as a
// pin. The popup mirrors that so its arrow/styling can line up with the two
// marker shapes.
const PIN_ZOOM_THRESHOLD = 8;

/** Coordinates are all `useMapPopup` needs from the active item. */
type ActivePopupItem = Pick<MapMarkerItem, 'latitude' | 'longitude'>;

export interface UseMapPopupParams {
    /**
     * The item the popup should point at. When `null`/`undefined` (or when it
     * lacks coordinates) any open popup is removed.
     */
    activeItem?: ActivePopupItem | null;
    /** Max popup width passed to Mapbox. Defaults to `'280px'`. */
    maxWidth?: string;
    /**
     * Whether to render popups on small (mobile) breakpoints. Defaults to
     * `false`, preserving the desktop-only affordance. Opt in when the consumer
     * provides a mobile popup experience.
     */
    allowOnMobile?: boolean;
}

export interface UseMapPopupResult {
    /**
     * Wrapper component whose children are rendered into the Mapbox popup DOM.
     * Render it once wherever the popup content lives; Mapbox moves the inner
     * element into the popup via `setDOMContent`.
     */
    MapPopup: (props: { children: React.ReactNode }) => React.ReactElement;
    /** Imperatively removes the current popup. */
    closePopup: () => void;
}

/**
 * Manages a single Mapbox `Popup` tied to the surrounding `MapProvider`.
 *
 * - Creates/repositions the popup whenever `activeItem` changes.
 * - Removes the popup when `activeItem` is cleared, on unmount, and before
 *   creating a replacement (so duplicates never stack up).
 * - No-ops on small breakpoints — popups are a desktop-only affordance.
 *
 * Content is provided via the returned `MapPopup` component. It renders a
 * host element that Mapbox relocates into the popup, so the popup body stays
 * a normal part of the React tree (state, events, and portaled context all
 * keep working).
 */
export function useMapPopup({
    activeItem,
    maxWidth = DEFAULT_MAX_WIDTH,
    allowOnMobile = false,
}: UseMapPopupParams): UseMapPopupResult {
    const { mapInstance } = useMap();
    const { isSmAndDown } = useBreakpoint();

    const popupElementRef = React.useRef<HTMLDivElement>(null);
    const popupRef = React.useRef<mapboxgl.Popup | null>(null);

    const closePopup = React.useCallback(() => {
        if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
        }
    }, []);

    React.useEffect(() => {
        if (!mapInstance) return;
        if (!popupElementRef.current) return;
        if (isSmAndDown && !allowOnMobile) return; // Popups are desktop-only unless opted in.
        if (isMapRemoved(mapInstance)) return;

        // Remove any existing popup first so we never stack duplicates.
        if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
        }

        if (activeItem?.longitude && activeItem?.latitude) {
            popupRef.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                closeOnMove: false,
                maxWidth,
                offset: POPUP_OFFSET,
                focusAfterOpen: false, // Focus is managed by the consumer's card.
            })
                .setLngLat([
                    parseFloat(activeItem.longitude),
                    parseFloat(activeItem.latitude),
                ])
                .setDOMContent(popupElementRef.current)
                .addTo(mapInstance);
        }

        const popup = popupRef.current;
        return () => {
            popup?.remove();
        };
    }, [activeItem, isSmAndDown, allowOnMobile, mapInstance, maxWidth]);

    const MapPopup = React.useCallback(
        ({ children }: { children: React.ReactNode }) => (
            <MapPopupHost mapInstance={mapInstance} popupElementRef={popupElementRef}>
                {children}
            </MapPopupHost>
        ),
        [mapInstance]
    );

    return React.useMemo(() => ({ MapPopup, closePopup }), [MapPopup, closePopup]);
}

interface MapPopupHostProps {
    children: React.ReactNode;
    popupElementRef: React.RefObject<HTMLDivElement>;
    mapInstance: Map | null;
}

/**
 * Renders the popup body inside a host element that Mapbox relocates into the
 * popup. The outer wrapper stays in the React tree so React never tries to
 * reconcile the moved node. A `map-popup-outer` class plus a `circle`/`pin`
 * modifier (driven by the current zoom) are applied so consumers can style
 * the popup to match the marker shape it points at.
 */
function MapPopupHost({ children, popupElementRef, mapInstance }: MapPopupHostProps) {
    const [markerType, setMarkerType] = React.useState<'circle' | 'pin'>('circle');

    React.useEffect(() => {
        if (!mapInstance) return;
        if (isMapRemoved(mapInstance)) return;

        const handleZoomChange = () => {
            setMarkerType(mapInstance.getZoom() >= PIN_ZOOM_THRESHOLD ? 'pin' : 'circle');
        };

        handleZoomChange();
        mapInstance.on('zoom', handleZoomChange);
        return () => {
            mapInstance.off('zoom', handleZoomChange);
        };
    }, [mapInstance]);

    return (
        <div>
            <div ref={popupElementRef} className={`map-popup-outer ${markerType}`}>
                {children}
            </div>
        </div>
    );
}

function isMapRemoved(map: Map): boolean {
    // `_removed` is an internal Mapbox flag that guards against operating on a
    // torn-down map (cleanup / HMR races).
    return (map as { _removed?: boolean })._removed === true;
}

// cSpell:ignore closebutton
